import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Shield, CheckCircle2, AlertCircle, Clock, MinusCircle,
  ChevronDown, ChevronRight, FileText, Lock, Users, Database,
  Activity, Eye, Download, RefreshCw, Edit2, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors and labels
const STATUS_CONFIG = {
  implemented: { 
    color: 'bg-emerald-500', 
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20',
    text: 'text-emerald-700 dark:text-emerald-400', 
    label: 'Implemented',
    icon: CheckCircle2
  },
  partial: { 
    color: 'bg-amber-500', 
    bgLight: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-400', 
    label: 'Partial',
    icon: Clock
  },
  not_implemented: { 
    color: 'bg-red-500', 
    bgLight: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-400', 
    label: 'Not Implemented',
    icon: AlertCircle
  },
  not_applicable: { 
    color: 'bg-gray-400', 
    bgLight: 'bg-gray-50 dark:bg-gray-800/50',
    text: 'text-gray-600 dark:text-gray-400', 
    label: 'N/A',
    icon: MinusCircle
  }
};

// Category icons
const CATEGORY_ICONS = {
  'A.5': FileText,
  'A.6': Users,
  'A.7': Users,
  'A.8': Database,
  'A.9': Lock,
  'A.10': Shield,
  'A.12': Activity,
  'A.16': AlertCircle,
  'A.18': CheckCircle2
};

export default function ComplianceDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [securityControls, setSecurityControls] = useState(null);
  const [auditSummary, setAuditSummary] = useState(null);
  const [dataRetention, setDataRetention] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingControl, setEditingControl] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', notes: '', evidence: '' });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('controls');

  useEffect(() => {
    loadDashboard();
  }, [token]);

  const loadDashboard = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [dashboardRes, controlsRes, auditRes, retentionRes] = await Promise.all([
        fetch(`${API}/api/compliance/dashboard`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/compliance/security-controls`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/compliance/audit-summary`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/compliance/data-retention`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (controlsRes.ok) setSecurityControls(await controlsRes.json());
      if (auditRes.ok) setAuditSummary(await auditRes.json());
      if (retentionRes.ok) setDataRetention(await retentionRes.json());
    } catch (error) {
      console.error('Failed to load compliance data:', error);
      toast.error('Failed to load compliance dashboard');
    }
    setLoading(false);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const openEditModal = (control) => {
    setEditingControl(control);
    setEditForm({
      status: control.status,
      notes: control.notes || '',
      evidence: control.evidence || ''
    });
  };

  const handleSaveControl = async () => {
    if (!editingControl) return;
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/compliance/controls/${editingControl.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success('Control status updated');
        setEditingControl(null);
        loadDashboard();
      } else {
        toast.error('Failed to update control');
      }
    } catch (error) {
      console.error('Error updating control:', error);
      toast.error('Failed to update control');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">Loading compliance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-dashboard">
      {/* Header with Summary */}
      <div className="rounded-xl p-6 border" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#4A3B32] flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl" style={{ color: 'var(--text-primary, #2C2420)' }}>ISO 27001 Compliance</h2>
              <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Information Security Management</p>
            </div>
          </div>
          <Button onClick={loadDashboard} variant="outline" size="sm" data-testid="refresh-compliance-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {dashboard && (
          <div className="grid grid-cols-4 gap-4">
            {/* Compliance Score */}
            <div className="col-span-2 p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary, #F5F3F1)' }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Compliance Score</span>
                <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{dashboard.summary.compliance_percentage}%</span>
              </div>
              <Progress value={dashboard.summary.compliance_percentage} className="h-3" />
              <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                {dashboard.summary.implemented} of {dashboard.summary.total_controls} controls implemented
              </p>
            </div>

            {/* Status Breakdown */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary, #F5F3F1)' }}>
              <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Implemented</span>
              <div className="flex items-center gap-2 mt-1">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-xl font-bold text-emerald-600">{dashboard.summary.implemented}</span>
              </div>
            </div>

            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary, #F5F3F1)' }}>
              <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>In Progress</span>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-5 h-5 text-amber-500" />
                <span className="text-xl font-bold text-amber-600">{dashboard.summary.partial}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
        {[
          { id: 'controls', label: 'Control Status', icon: Shield },
          { id: 'security', label: 'Security Controls', icon: Lock },
          { id: 'audit', label: 'Audit Summary', icon: Activity },
          { id: 'retention', label: 'Data Retention', icon: Database }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.id 
                ? 'border-[#4A3B32] text-[#4A3B32]' 
                : 'border-transparent hover:border-gray-300'
            }`}
            style={{ color: activeTab === tab.id ? '#4A3B32' : 'var(--text-secondary)' }}
            data-testid={`compliance-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Controls Tab */}
      {activeTab === 'controls' && dashboard && (
        <div className="space-y-4">
          {dashboard.categories.map(category => {
            const CategoryIcon = CATEGORY_ICONS[category.id] || Shield;
            const isExpanded = expandedCategories[category.id];
            const implementedPct = Math.round((category.stats.implemented / category.stats.total) * 100);
            
            return (
              <div 
                key={category.id} 
                className="rounded-xl border overflow-hidden"
                style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-hover)] transition-colors"
                  data-testid={`category-${category.id}`}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    <div className="w-10 h-10 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
                      <CategoryIcon className="w-5 h-5 text-[#4A3B32]" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>{category.id}: {category.title}</h3>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{category.stats.total} controls</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${implementedPct}%` }} />
                      </div>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{implementedPct}%</span>
                    </div>
                    <div className="flex gap-1">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700">{category.stats.implemented}</span>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{category.stats.partial}</span>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">{category.stats.not_implemented}</span>
                    </div>
                  </div>
                </button>

                {/* Controls List */}
                {isExpanded && (
                  <div className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                    {category.controls.map(control => {
                      const statusConfig = STATUS_CONFIG[control.status] || STATUS_CONFIG.not_implemented;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <div
                          key={control.id}
                          className="flex items-start gap-4 p-4 border-b last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
                          style={{ borderColor: 'var(--border-color)' }}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${statusConfig.bgLight}`}>
                            <StatusIcon className={`w-4 h-4 ${statusConfig.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h4 className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                                  {control.id}: {control.name}
                                </h4>
                                <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--text-muted)' }}>
                                  {control.description}
                                </p>
                                {control.notes && (
                                  <p className="text-xs mt-2 italic" style={{ color: 'var(--text-secondary)' }}>
                                    Notes: {control.notes}
                                  </p>
                                )}
                                {control.last_reviewed && (
                                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                    Last reviewed: {new Date(control.last_reviewed).toLocaleDateString()} by {control.reviewed_by}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className={`px-2 py-1 text-xs rounded-full ${statusConfig.bgLight} ${statusConfig.text}`}>
                                  {statusConfig.label}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditModal(control)}
                                  data-testid={`edit-control-${control.id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Security Controls Tab */}
      {activeTab === 'security' && securityControls && (
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(securityControls).map(([category, controls]) => (
            <div 
              key={category} 
              className="rounded-xl border p-5"
              style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
            >
              <h3 className="font-medium capitalize mb-4" style={{ color: 'var(--text-primary)' }}>
                {category.replace(/_/g, ' ')}
              </h3>
              <div className="space-y-3">
                {Object.entries(controls).map(([name, info]) => (
                  <div key={name} className="flex items-start gap-3">
                    {info.enabled ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{info.description}</p>
                      {info.algorithm && (
                        <span className="text-xs px-2 py-0.5 rounded bg-[#4A3B32]/10 text-[#4A3B32] mt-1 inline-block">
                          {info.algorithm}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Audit Summary Tab */}
      {activeTab === 'audit' && auditSummary && (
        <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
          <h3 className="font-medium mb-6" style={{ color: 'var(--text-primary)' }}>Security Audit Summary (Last 30 Days)</h3>
          <div className="grid grid-cols-4 gap-6">
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <Activity className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{auditSummary.audit_events_30d}</p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Audit Events</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <Lock className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{auditSummary.login_events_30d}</p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Login Events</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <Users className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{auditSummary.active_users}</p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Active Users</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <Shield className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{auditSummary['2fa_adoption_rate']}%</p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>2FA Adoption</p>
            </div>
          </div>
          <p className="text-xs mt-4 text-right" style={{ color: 'var(--text-muted)' }}>
            Generated: {new Date(auditSummary.generated_at).toLocaleString()}
          </p>
        </div>
      )}

      {/* Data Retention Tab */}
      {activeTab === 'retention' && dataRetention && (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
          <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>Data Retention Policies</h3>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Last reviewed: {dataRetention.last_reviewed} | Next review: {dataRetention.next_review}
            </p>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                <th className="text-left p-3 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Data Type</th>
                <th className="text-left p-3 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Retention Period</th>
                <th className="text-left p-3 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Deletion Policy</th>
                <th className="text-left p-3 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {dataRetention.policies.map((policy, idx) => (
                <tr key={idx} className="border-b last:border-b-0" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="p-3 font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{policy.data_type}</td>
                  <td className="p-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{policy.retention_period}</td>
                  <td className="p-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{policy.deletion_policy}</td>
                  <td className="p-3 text-xs" style={{ color: 'var(--text-muted)' }}>{policy.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Control Modal */}
      <Dialog open={!!editingControl} onOpenChange={() => setEditingControl(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Control Status</DialogTitle>
            <DialogDescription>
              {editingControl?.id}: {editingControl?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger data-testid="control-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="implemented">Implemented</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="not_implemented">Not Implemented</SelectItem>
                  <SelectItem value="not_applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Implementation Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Describe how this control is implemented..."
                rows={3}
                data-testid="control-notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Evidence / Reference</Label>
              <Input
                value={editForm.evidence}
                onChange={(e) => setEditForm(prev => ({ ...prev, evidence: e.target.value }))}
                placeholder="e.g., Policy document, code file, configuration"
                data-testid="control-evidence-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingControl(null)}>Cancel</Button>
            <Button onClick={handleSaveControl} disabled={saving} className="bg-[#4A3B32] hover:bg-[#3A2B22]" data-testid="save-control-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
